import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { Container, Spinner, Toast, ToastContainer, Form } from 'react-bootstrap';
import { FiLogOut, FiCalendar, FiGrid, FiMenu } from 'react-icons/fi';
import { getUser, logout as logoutAuth, saveUser } from '../utils/auth';
import { authAPI, departmentAPI, projectAPI, userAPI } from '../utils/api';
import { getAppSettings, getLandingPath } from '../utils/settings';
import { normalizeAlphaNumLower } from '../utils/string';
import {
  FINANCE_DASHBOARD_PROJECTS,
  OM_DASHBOARD_PROJECTS,
  HR_DASHBOARD_PROJECTS,
  FINANCE_DUPLICATE_NAME_SET,
  OM_DUPLICATE_NAME_SET,
  HR_DUPLICATE_NAME_SET
} from '../utils/dashboardProjects';
import { isOmDepartmentKey, isHrDepartmentKey } from '../utils/departmentKeys';
import './Navbar.css';

const normalizeDepartmentKey = (value) => normalizeAlphaNumLower(value);

const getDepartmentPriority = (departmentName) => {
  const key = normalizeDepartmentKey(departmentName);
  if (key === 'finance') return 0;
  if (isOmDepartmentKey(key)) return 1;
  return 2;
};

const sortDepartmentsForTabs = (departmentRows) => (
  [...(Array.isArray(departmentRows) ? departmentRows : [])].sort((a, b) => {
    const priorityDiff = getDepartmentPriority(a?.name) - getDepartmentPriority(b?.name);
    if (priorityDiff !== 0) return priorityDiff;
    return String(a?.name || '').localeCompare(String(b?.name || ''), undefined, { sensitivity: 'base' });
  })
);

const IT_ASSET_PROJECT_CODE_BY_TAB = Object.freeze({
  dashboard: 'ITASSET_DASH',
  my: 'ITASSET_MY',
  master: 'ITASSET_MSTR',
  inventory: 'ITASSET_ALOC',
  history: 'ITASSET_HIST',
  user_history: 'ITASSET_USRHIST',
  audit: 'ITASSET_AUDT',
});

const normalizeProjectName = (value) => normalizeAlphaNumLower(value);

const EXCO_ALL_DASHBOARDS = '__ALL__';

const getExcoDashboardIdsForDepartment = (user, departmentId) => {
  const roleType = String(user?.roleType || user?.role?.roleType || '').toUpperCase();
  if (roleType === 'ADMIN') return [EXCO_ALL_DASHBOARDS];

  const dashboardAccess = user?.excoDashboardAccess || {};
  const hasExplicitDashboardAccess = Object.keys(dashboardAccess).length > 0;
  const direct = dashboardAccess?.[departmentId];
  const stringKey = dashboardAccess?.[String(departmentId)];
  const explicitIds = Array.isArray(direct) ? direct : (Array.isArray(stringKey) ? stringKey : []);

  if (hasExplicitDashboardAccess) return explicitIds;

  const assignedDepartmentIds = Array.isArray(user?.departmentIds)
    ? user.departmentIds.map((value) => Number(value)).filter((value) => Number.isInteger(value) && value > 0)
    : [];

  return assignedDepartmentIds.includes(Number(departmentId)) ? [EXCO_ALL_DASHBOARDS] : [];
};

const Navbar = ({ setIsLoggedIn, setUser: setAppUser }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedDept, setSelectedDept] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deptLoading, setDeptLoading] = useState(true);
  const [projectLoading, setProjectLoading] = useState(true);
  const [appSettings, setAppSettings] = useState(getAppSettings());
  const [showLogoutToast, setShowLogoutToast] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const landingPath = getLandingPath(appSettings.defaultLandingPage);

  /**
   * Load user data and departments on mount
   */
  useEffect(() => {
    const userData = getUser();
    if (userData) {
      setUser(userData);
    }

    // Fetch departments and projects
    fetchDepartments();
    fetchProjects();

    // Update date every minute
    const dateInterval = setInterval(() => {
      setCurrentDate(new Date());
    }, 60000);

    const handleAppSettingsChanged = () => {
      const nextSettings = getAppSettings();
      setAppSettings(nextSettings);
    };

    globalThis.addEventListener('appSettingsChanged', handleAppSettingsChanged);

    return () => {
      clearInterval(dateInterval);
      globalThis.removeEventListener('appSettingsChanged', handleAppSettingsChanged);
    };
  }, []);

  useEffect(() => {
    const syncCurrentUserAccess = async () => {
      const currentUser = getUser();
      const userId = Number(currentUser?.id);
      if (!Number.isInteger(userId) || userId <= 0) return;

      try {
        const response = await userAPI.getUserById(userId);
        if (!response?.success || !response?.data) return;

        const mergedUser = {
          ...currentUser,
          ...response.data,
          departmentIds: Array.isArray(response.data.departmentIds) ? response.data.departmentIds : (currentUser?.departmentIds || []),
          excoDashboardAccess: response.data.excoDashboardAccess || currentUser?.excoDashboardAccess || {}
        };

        saveUser(mergedUser);
        setUser(mergedUser);
      } catch (error) {
        if (error?.statusCode === 403) return; // user doesn't have users:read permission
        console.error('Error refreshing current user access:', error);
      }
    };

    syncCurrentUserAccess();

    // Refresh on window focus, but throttled above.
    globalThis.addEventListener('focus', syncCurrentUserAccess);
    return () => globalThis.removeEventListener('focus', syncCurrentUserAccess);
  }, []);

  /**
   * Fetch departments
   */
  const fetchDepartments = async () => {
    try {
      setDeptLoading(true);
      const response = await departmentAPI.getAllDepartments();
      if (response.success && response.data) {
        const sortedDepartments = sortDepartmentsForTabs(response.data);
        setDepartments(sortedDepartments);
        const savedDeptRaw = localStorage.getItem('selectedDept');
        const savedDept = savedDeptRaw ? JSON.parse(savedDeptRaw) : null;
        const matchedSavedDept = savedDept
          ? sortedDepartments.find((dept) => Number(dept.id) === Number(savedDept.id))
          : null;

        if (matchedSavedDept) {
          setSelectedDept(matchedSavedDept);
        } else if (sortedDepartments.length > 0) {
          setSelectedDept(sortedDepartments[0]);
          localStorage.setItem('selectedDept', JSON.stringify(sortedDepartments[0]));
          // Notify dashboard page that the active dept changed (stale dept was replaced)
          globalThis.dispatchEvent(new Event('departmentChanged'));
        }
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
    } finally {
      setDeptLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      setProjectLoading(true);
      const response = await projectAPI.getAllProjects();
      const projectRows = Array.isArray(response?.data) ? response.data : [];
      setProjects(projectRows);

      // Restore selectedProject from localStorage
      const savedProjectRaw = localStorage.getItem('selectedProject');
      if (savedProjectRaw) {
        try {
          setSelectedProject(JSON.parse(savedProjectRaw));
        } catch (_) {}
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
      setProjects([]);
    } finally {
      setProjectLoading(false);
    }
  };

  const getProjectsForDepartment = useCallback((department, allProjects) => {
    const deptId = Number(department?.id);
    if (!deptId) return [];

    const deptProjects = (Array.isArray(allProjects) ? allProjects : [])
      .filter((project) => Number(project.departmentId) === deptId);
    const normalizedDepartment = normalizeDepartmentKey(department?.name);
    const roleType = String(user?.roleType || user?.role?.roleType || '').toUpperCase();

    // IT & Automation: EXCO should only see "My Assets"
    if (normalizedDepartment === 'itautomation' || normalizedDepartment === 'itandautomation' || normalizedDepartment === 'it' || normalizedDepartment === 'itautomationdepartment') {
      if (roleType === 'EXCO') {
        return deptProjects.filter((project) => {
          const code = String(project?.projectCode || '').trim().toUpperCase();
          const name = normalizeProjectName(project?.name);
          return code === 'ITASSET_MY' || name === 'myassets';
        });
      }
    }

    const excoDashboardIds = getExcoDashboardIdsForDepartment(user, deptId);
    const filterForExco = (items) => {
      if (excoDashboardIds.includes(EXCO_ALL_DASHBOARDS)) return items;
      if (!excoDashboardIds.length) return [];
      return items.filter((item) => excoDashboardIds.includes(String(item.id)));
    };

    if (normalizedDepartment !== 'finance') {

      if (isHrDepartmentKey(normalizedDepartment)) {
        const hrRealProjects = deptProjects.filter(
          (project) => !HR_DUPLICATE_NAME_SET.has(normalizeProjectName(project?.name))
        );
        const hrProjects = HR_DASHBOARD_PROJECTS.map((project) => ({ ...project, departmentId: deptId }));
        return filterForExco([...hrProjects, ...hrRealProjects]);
      }

      if (!isOmDepartmentKey(normalizedDepartment)) {
        return filterForExco(deptProjects);
      }

      const omRealProjects = deptProjects.filter(
        (project) => !OM_DUPLICATE_NAME_SET.has(normalizeProjectName(project?.name))
      );

      const omProjects = OM_DASHBOARD_PROJECTS.map((project) => ({
        ...project,
        departmentId: deptId
      }));

      return filterForExco([...omProjects, ...omRealProjects]);
    }

    const financeRealProjects = deptProjects.filter(
      (project) => !FINANCE_DUPLICATE_NAME_SET.has(normalizeProjectName(project?.name))
    );

    const financeProjects = FINANCE_DASHBOARD_PROJECTS.map((project) => ({
      ...project,
      departmentId: deptId
    }));

    return filterForExco([...financeProjects, ...financeRealProjects]);
  }, [user]);

  /**
   * Handle department selection
   */
  const handleDeptSelect = (dept) => {
    const availableProjects = getProjectsForDepartment(dept, projects);
    const normalizedDepartment = normalizeDepartmentKey(dept?.name);
    const defaultProject = isHrDepartmentKey(normalizedDepartment)
      ? availableProjects.find((project) => String(project.id).trim().toLowerCase() === 'hr-main-dashboard') || availableProjects[0] || null
      : availableProjects[0] || null;

    setSelectedDept(dept);
    localStorage.setItem('selectedDept', JSON.stringify(dept));
    setSelectedProject(defaultProject);

    if (defaultProject) {
      localStorage.setItem('selectedProject', JSON.stringify(defaultProject));
    } else {
      localStorage.removeItem('selectedProject');
    }

    globalThis.dispatchEvent(new Event('departmentChanged'));
    globalThis.dispatchEvent(new Event('projectChanged'));
    navigate('/dashboard');
  };

  const handleProjectSelect = (event) => {
    const projectId = String(event.target.value || '').trim();
    const availableProjects = getProjectsForDepartment(selectedDept, projects);
    const chosenProject = availableProjects.find((project) => String(project.id) === projectId) || null;
    setSelectedProject(chosenProject);

    if (chosenProject) {
      localStorage.setItem('selectedProject', JSON.stringify(chosenProject));
    } else {
      localStorage.removeItem('selectedProject');
    }

    globalThis.dispatchEvent(new Event('projectChanged'));
    // If user is on a deep page (e.g. approvals), switching dashboard should take them back
    // to the main dashboard renderer immediately without requiring refresh.
    navigate('/dashboard');
  };

  const projectOptionsForSelectedDept = useMemo(
    () => getProjectsForDepartment(selectedDept, projects),
    [selectedDept, projects, getProjectsForDepartment]
  );

  const lastItAssetsSyncedProjectIdRef = useRef('');
  useEffect(() => {
    // When user is inside IT Assets (its internal tabs live under /dashboard in
    // normal usage, not /it-assets directly), keep the dashboard dropdown in
    // sync with the current sub-tab via URL query params (no refresh needed).
    // Previously this only matched `/it-assets`, which the app never actually
    // navigates to during normal use — so this sync silently never ran.
    const path = String(location?.pathname || '');
    if (!path.startsWith('/it-assets') && !path.startsWith('/dashboard')) return;
    if (!selectedDept) return;

    const params = new URLSearchParams(String(location?.search || ''));
    const tab = String(params.get('tab') || 'my').trim().toLowerCase();
    const desiredProjectCode = IT_ASSET_PROJECT_CODE_BY_TAB[tab] || IT_ASSET_PROJECT_CODE_BY_TAB.my;
    if (!desiredProjectCode) return;

    const match = projectOptionsForSelectedDept.find(
      (project) => String(project?.projectCode || '').trim() === desiredProjectCode
    );
    if (!match) return;

    const currentId = String(selectedProject?.id ?? '').trim();
    const nextId = String(match?.id ?? '').trim();
    if (currentId && nextId && currentId === nextId) return;
    if (nextId && lastItAssetsSyncedProjectIdRef.current === nextId) return;

    setSelectedProject(match);
    // Persist selection so it survives refresh/navigation, but avoid broadcasting
    // `projectChanged` because that can trigger expensive re-fetch loops elsewhere.
    localStorage.setItem('selectedProject', JSON.stringify(match));
    lastItAssetsSyncedProjectIdRef.current = nextId;
  }, [location.pathname, location.search, selectedDept, projectOptionsForSelectedDept, selectedProject]);

  useEffect(() => {
    if (!selectedDept) return;

    const selectedProjectId = String(selectedProject?.id || '').trim();
    if (!selectedProjectId) return;

    const isStillAllowed = projectOptionsForSelectedDept.some(
      (project) => String(project.id) === selectedProjectId
    );

    if (!isStillAllowed) {
      const fallbackProject = projectOptionsForSelectedDept[0] || null;
      setSelectedProject(fallbackProject);

      if (fallbackProject) {
        localStorage.setItem('selectedProject', JSON.stringify(fallbackProject));
      } else {
        localStorage.removeItem('selectedProject');
      }

      globalThis.dispatchEvent(new Event('projectChanged'));
    }
  }, [selectedDept, selectedProject, projectOptionsForSelectedDept]);

  useEffect(() => {
    const syncDepartmentSelection = () => {
      const savedDeptRaw = localStorage.getItem('selectedDept');
      const savedDept = savedDeptRaw ? JSON.parse(savedDeptRaw) : null;
      if (!savedDept?.id) return;

      const matchedDepartment = departments.find((dept) => Number(dept.id) === Number(savedDept.id));
      if (matchedDepartment) {
        setSelectedDept(matchedDepartment);
      }
    };

    globalThis.addEventListener('departmentChanged', syncDepartmentSelection);
    return () => globalThis.removeEventListener('departmentChanged', syncDepartmentSelection);
  }, [departments]);

  useEffect(() => {
    const syncProjectSelection = () => {
      const savedProjectRaw = localStorage.getItem('selectedProject');
      const savedProject = savedProjectRaw ? JSON.parse(savedProjectRaw) : null;
      setSelectedProject(savedProject || null);
    };

    globalThis.addEventListener('projectChanged', syncProjectSelection);
    return () => globalThis.removeEventListener('projectChanged', syncProjectSelection);
  }, []);

  /**
   * Handle logout
   */
  const handleLogout = async () => {
    try {
      setLoading(true);
      await authAPI.logout();
      logoutAuth();
      setIsLoggedIn(false);
      if (setAppUser) setAppUser(null);
      setShowLogoutToast(true);

      setTimeout(() => {
        navigate('/login');
      }, 1500);
    } catch (error) {
      console.error('Logout error:', error);
      logoutAuth();
      setIsLoggedIn(false);
      if (setAppUser) setAppUser(null);
      navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Format current date
   */
  const formatDate = () => {
    return currentDate.toLocaleDateString(appSettings.dateFormat || 'en-IN', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (!user) {
    return (
      <div className="navbar-dept-tabs">
        <Container fluid className="d-flex justify-content-center">
          <Spinner animation="border" size="sm" className="text-primary" />
        </Container>
      </div>
    );
  }

  return (
    <>
      {/* Department Tabs Bar */}
      <div className="navbar-dept-tabs">
        <Container fluid>
          <div className="dept-tabs-wrapper">
            {/* Left - Company Logo + DISHA + Date + Departments */}
            <div className="dept-tabs-left">
              {/* Mobile menu toggle — opens the off-canvas Sidebar drawer. No
                  shared state/context with Sidebar, so this follows the same
                  custom-event pattern already used elsewhere in this file
                  (e.g. 'departmentChanged'/'projectChanged'). */}
              <button
                type="button"
                className="navbar-hamburger"
                onClick={() => globalThis.dispatchEvent(new Event('toggleMobileSidebar'))}
                aria-label="Toggle navigation menu"
              >
                <FiMenu size={20} />
              </button>

              {/* Company Logo + DISHA */}
              <Link to={landingPath} className="navbar-brand">
                <div className="brand-logo">
                  <img src="https://inveniatech.com/wp-content/uploads/2024/08/Invenia-logo.png" alt="Invenia" className="company-logo" />
                  <span className="brand-text">DISHA</span>
                </div>
              </Link>

              {/* Current Date */}
              <div className="navbar-date">
                <FiCalendar size={13} className="date-icon" />
                <span className="date-text">{formatDate()}</span>
              </div>

              {/* Department Tabs */}
              {deptLoading ? (
                <Spinner animation="border" size="sm" className="text-primary" />
              ) : departments.length === 0 ? (
                <div className="no-dept-message">
                  <p>No departments</p>
                </div>
              ) : (
                <div className="dept-tabs-scroll">
                  {departments.map(dept => (
                    <button
                      key={dept.id}
                      className={`dept-tab ${selectedDept?.id === dept.id ? 'active' : ''}`}
                      onClick={() => handleDeptSelect(dept)}
                      title={dept.description}
                    >
                      <FiGrid size={16} className="dept-icon" />
                      <span className="dept-name">{dept.name}</span>
                    </button>
                  ))}

                </div>
              )}

              <div className="project-select-wrap">
                <Form.Label htmlFor="navbar-project-select" className="project-select-label">Select Dashboard</Form.Label>
                <Form.Select
                  id="navbar-project-select"
                  className="project-select"
                  value={selectedProject?.id || ''}
                  onChange={handleProjectSelect}
                  disabled={projectLoading || !selectedDept}
                >
                  {!selectedDept ? (
                    <option value="">Select department first</option>
                  ) : projectLoading ? (
                    <option value="">Loading projects...</option>
                  ) : (
                    <>
                      <option value="">Select dashboard</option>
                      {projectOptionsForSelectedDept.map((project) => (
                          <option key={project.id} value={String(project.id)}>
                            {project.name}
                          </option>
                        ))}
                    </>
                  )}
                </Form.Select>
              </div>
            </div>

            {/* Right - Notifications & Logout */}
            <div className="dept-tabs-right">

              <button
                type="button"
                className="navbar-logout-btn"
                onClick={handleLogout}
                disabled={loading}
                title="Logout"
                aria-label="Logout from application"
              >
                {loading ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" className="me-2" />
                    Logging out...
                  </>
                ) : (
                  <>
                    <FiLogOut size={16} className="me-2" />
                    Logout
                  </>
                )}
              </button>

            </div>
          </div>
        </Container>
      </div>

      {/* Logout Toast */}
      <ToastContainer position="top-end" className="p-3">
        <Toast
          show={showLogoutToast}
          onClose={() => setShowLogoutToast(false)}
          delay={2000}
          autohide
          className="logout-toast"
        >
          <Toast.Body className="bg-success text-white">
            ✓ Logged out successfully
          </Toast.Body>
        </Toast>
      </ToastContainer>
    </>
  );
};

export default Navbar;
